import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cosmeticById,
  type ChannelAnswer,
  type ChannelDigest,
  type CosmeticInfo,
  type EngagementAnalytics,
  type EngagementProfile,
  type EngagementStanding,
  type FocusSessionView,
  type FocusSummary,
  type Kudos,
  type MorningDigest,
  type WorkspaceHealthDigest,
} from '@itsappy/shared';
import type { ChallengeMetric, TeamChallenge, WeeklyRecap } from '@itsappy/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/store/auth';

export interface EngagementMe {
  profile: EngagementProfile;
  store: CosmeticInfo[];
}

/** An equipped slot holds a cosmetic id; what it renders as is catalogue data. */
const cosmeticValue = (id?: string) => (id ? cosmeticById(id)?.value ?? id : undefined);

/**
 * Buy and equip both come back with the whole profile, so the cache takes the
 * server's answer instead of guessing at balances — a balance can always be
 * explained, and this is where the client keeps that promise.
 */
function acceptProfile(
  qc: ReturnType<typeof useQueryClient>,
  profile: EngagementProfile,
) {
  qc.setQueryData<EngagementMe>(['engagement', 'me'], (old) =>
    old ? { ...old, profile } : old,
  );
}

export function useEngagementMe(enabled = true) {
  return useQuery({
    queryKey: ['engagement', 'me'],
    queryFn: () => api.get<EngagementMe>('/engagement/me'),
    staleTime: 15_000,
    enabled,
  });
}

/**
 * The reaction that gives an Appy in the active workspace, or null when the
 * owner has not switched reaction Appys on. Read off the session's copy of
 * the workspace — `/auth/me` carries settings — so chat can show the hint
 * without a request of its own.
 */
export function useKudosEmoji(): string | null {
  return useAuth((state) => {
    if (!state.features.includes('engagement')) return null;
    const org = state.organizations.find((item) => item.id === state.orgId);
    return org?.settings?.engagement?.kudosEmoji ?? null;
  });
}

export function useLeaderboard(period: 'week' | 'all', enabled = true) {
  return useQuery({
    queryKey: ['engagement', 'leaderboard', period],
    queryFn: () =>
      api.get<{ period: string; standings: EngagementStanding[] }>(
        `/engagement/leaderboard?period=${period}`,
      ),
    staleTime: 30_000,
    enabled,
  });
}

/** The workspace kudos feed — latest 50, live via the socket invalidation. */
export function useKudosFeed(enabled = true) {
  return useQuery({
    queryKey: ['engagement', 'kudos'],
    queryFn: () => api.get<{ items: Kudos[] }>('/engagement/kudos'),
    enabled,
  });
}

export function useGiveKudos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { toUserId: string; message: string; emoji?: string }) =>
      api.post<Kudos>('/engagement/kudos', body),
    // The broadcast refreshes the feed for everybody including the sender;
    // what the sender alone needs re-read is their own allowance and points.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['engagement', 'me'] });
      void qc.invalidateQueries({ queryKey: ['engagement', 'kudos'] });
    },
  });
}

export function useBuyStoreItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      api.post<{ profile: EngagementProfile }>('/engagement/store/buy', { itemId }),
    onSuccess: ({ profile }) => acceptProfile(qc, profile),
  });
}

export function useEquipCosmetic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title?: string | null; avatarRing?: string | null }) =>
      api.post<{ profile: EngagementProfile }>('/engagement/store/equip', body),
    onSuccess: ({ profile }) => {
      acceptProfile(qc, profile);
      // Flair rides on PublicUser, so the wearer's own avatar everywhere in
      // the app has to hear about the change without a reload.
      const { user, setUser } = useAuth.getState();
      if (user) {
        setUser({
          ...user,
          flairTitle: cosmeticValue(profile.equipped.title),
          flairRing: cosmeticValue(profile.equipped.avatarRing),
        });
      }
      void qc.invalidateQueries({ queryKey: ['members'] });
      void qc.invalidateQueries({ queryKey: ['engagement', 'leaderboard'] });
    },
  });
}

export function useFocusSummary(enabled = true) {
  return useQuery({
    queryKey: ['engagement', 'focus'],
    queryFn: () => api.get<FocusSummary>('/engagement/focus/summary'),
    // The week board moves as other people finish sessions, not only as you do.
    refetchInterval: 60_000,
    enabled,
  });
}

export function useStartFocus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { minutes: number; taskId?: string }) =>
      api.post<FocusSessionView>('/engagement/focus/start', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['engagement', 'focus'] });
      // Starting focus flips your presence to busy.
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
  });
}

export function useStopFocus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<FocusSessionView>('/engagement/focus/stop'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['engagement', 'focus'] });
      void qc.invalidateQueries({ queryKey: ['engagement', 'me'] });
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
  });
}

/* ----------------------------- challenges ------------------------------ */

/** The workspace's team challenges — active first, recent history after. */
export function useChallenges(enabled = true) {
  return useQuery({
    queryKey: ['engagement', 'challenges'],
    queryFn: () => api.get<{ items: TeamChallenge[] }>('/engagement/challenges'),
    staleTime: 30_000,
    enabled,
  });
}

/** Admin/owner only. The server refuses a fourth active challenge with a 400. */
export function useCreateChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      metric: ChallengeMetric;
      target: number;
      rewardPoints: number;
      title?: string;
      days?: number;
    }) => api.post<TeamChallenge>('/engagement/challenges', body),
    // The broadcast reaches everyone else; the creator refetches directly so
    // the new challenge is on their screen before the socket round trip.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['engagement', 'challenges'] });
    },
  });
}

/** Admin/owner only. Cancelling keeps the row, as history rather than a goal. */
export function useCancelChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (challengeId: string) =>
      api.post<TeamChallenge>(`/engagement/challenges/${challengeId}/cancel`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['engagement', 'challenges'] });
    },
  });
}

/* -------------------------------- recap --------------------------------- */

/**
 * One person's week as a shareable card.
 *
 * `week` is either an ISO week id (`2026-W35`), `current` for the week still
 * running, or nothing at all for the most recent finished week — which is
 * what a recap is normally about. Finished weeks are immutable and cached on
 * the server, so they are held here for the session rather than refetched.
 */
export function useWeeklyRecap(week?: string, enabled = true) {
  return useQuery({
    queryKey: ['engagement', 'recap', week ?? 'default'],
    queryFn: () =>
      api.get<WeeklyRecap>(
        `/engagement/recap${week ? `?week=${encodeURIComponent(week)}` : ''}`,
      ),
    staleTime: 60_000,
    enabled,
  });
}

/** Admin/owner only — is the retention loop actually looping? */
export function useEngagementAnalytics(enabled: boolean) {
  return useQuery({
    queryKey: ['engagement', 'analytics'],
    queryFn: () => api.get<EngagementAnalytics>('/engagement/analytics'),
    staleTime: 60_000,
    enabled,
  });
}

/* ------------------------------- digests ------------------------------- */

/**
 * Whether AI digests can answer in this deployment. Every digest button waits
 * on this, so a workspace with no key configured never sees a control that
 * fails when pressed. Cached for the session, like the assistant status.
 */
export function useDigestsStatus(enabled = true) {
  return useQuery({
    queryKey: ['digests', 'status'],
    queryFn: () => api.get<{ enabled: boolean }>('/digests/status'),
    staleTime: Infinity,
    enabled,
  });
}

/** "Catch me up" on a channel's unread messages. Costs AI tokens: on demand. */
export function useChannelDigest() {
  return useMutation({
    mutationFn: (channelId: string) =>
      api.post<ChannelDigest>(`/digests/channel/${channelId}`),
  });
}

/**
 * "Ask the channel": one question answered out of a conversation's history.
 *
 * A mutation for the same reason the digest is one — it spends tokens, so it
 * runs when a person asks and never on a cache's schedule. Gated by the same
 * `/digests/status` the catch-up button waits on.
 */
export function useAskChannel() {
  return useMutation({
    mutationFn: ({ channelId, question }: { channelId: string; question: string }) =>
      api.post<ChannelAnswer>(`/digests/channels/${channelId}/ask`, { question }),
  });
}

/** A mutation rather than a query on purpose: generating one costs AI tokens. */
export function useMorningDigest() {
  return useMutation({
    mutationFn: () => api.get<MorningDigest>('/digests/morning'),
  });
}

/** Admin/owner only, and fetched on demand for the same token-cost reason. */
export function useWorkspaceHealth() {
  return useMutation({
    mutationFn: () => api.get<WorkspaceHealthDigest>('/digests/workspace-health'),
  });
}
